import { useCallback, useEffect, useState } from 'react'
import {Button, DatePicker, Input, Modal, Select, Space, TableProps} from 'antd'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import {apiDeleteStatistical, apiGetListPartner, apiGetListStatistical} from '../../api'
import { PartnerList } from '../../interfaces/list-partner.interface'
import dayjs, { Dayjs } from 'dayjs'
import type { DatePickerProps } from 'antd';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatCurrency } from "../../utils/string-utils";
import { mapCompanyAccounts } from '../../assets/constants/constants'
import {EditOutlined, EyeOutlined, DeleteOutlined} from "@ant-design/icons";
import {STATISTICAL, CREATE_STATISTICAL, EDIT_STATISTICAL, REVENUES} from "../../assets/constants/route_path";
import {Link, useSearchParams} from "react-router-dom";
import ViewStatistical from "./ViewStatistical";
import moment from "moment";
import {toastError, toastSuccess} from "../../utils/toast";

const initCustomer = {
    partners: [],
    total: 0
}

const typeReport = [
    {
        name: "Theo tháng",
        value: "typeMonth",
    },
    {
        name: "Theo ngày",
        value: "typeDay",
    },
]

const dateFormat = 'YYYY-MM-DD';

dayjs.extend(customParseFormat);

export default function StatisticalPage() {
    const [customers, setCustomers] = useState<PartnerList>(initCustomer);
    const [statistical, setStatistical] = useState<any>([]);
    const [currentStatistical, setCurrentStatistical] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE);
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT);
    const [selectedCustomerDataCode, setSelectedCustomerCode] = useState<number>(0);
    const [selectedTypeReport, setSelectedTypeReport] = useState(typeReport[0].value);
    const [dates, setDates] = useState<any>();
    const [months, setMonths] = useState<Dayjs | null>(dayjs());
    const [isModalViewOpen, setIsModalViewOpen] = useState(false);
    const { setIsLoading } = useLoading();
    const [searchParams] = useSearchParams();
    const PrevCpCode = searchParams.get('cpCode');
    const [modalDelete, setModalDelete] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    });
    const [OTPConfirm, setOTPConfirm] = useState('');

    const columns: TableProps<any>['columns'] = [
        {
            key: 'statisticalDate',
            title: "Thời gian",
            render: (value: any) => {
                return getDate(value);
            },
        },
        {
            key: 'balanceStartDate',
            title: "Dư đầu ngày",
            dataIndex: 'balanceStartDate',
            render: (balanceStartDate) => formatCurrency(balanceStartDate)
        },
        {
            key: 'balanceEndDate',
            title: "Dư cuối ngày",
            dataIndex: 'balanceEndDate',
            render: (balanceEndDate) => formatCurrency(balanceEndDate)
        },
        {
            key: 'revenue',
            title: "Bán trong ngày",
            dataIndex: 'revenue',
            render: (revenue) => formatCurrency(revenue)
        },
        {
            key: 'revenueNet',
            title: "Bán trong ngày sau CK",
            dataIndex: 'revenueNet',
            render: (revenueNet) => formatCurrency(revenueNet)
        },
        {
            key: 'description',
            title: "Mô tả",
            dataIndex: 'description',
        },
        {
            key: 'action',
            title: "",
            render: (_, record) => (
                <Space>
                    <Button
                        onClick={() => {
                            setIsModalViewOpen(true);
                            setCurrentStatistical(record.id);
                        }}
                        style={{ color: 'white', background: 'gray' }}
                        icon={<EyeOutlined />}
                    >
                    </Button>
                    <Link to={REVENUES + STATISTICAL + EDIT_STATISTICAL + `?id=${record.id}`}>
                        <Button
                            style={{ color: 'white', background: 'orange' }}
                            icon={<EditOutlined />}
                        />
                    </Link>
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            setModalDelete({
                                data: record,
                                isOpen: true,
                            });
                        }}
                    />
                </Space>
            ),
        },
    ];

    const getDate = (value: any) => {
        return moment([value?.year, value?.month - 1, value?.day]).format('DD/MM/YYYY');
    }

    const handleDelete = async (id: string) => {
        try {
            setIsLoading(true);
            const resData = await apiDeleteStatistical({
                id: id,
                OTPConfirm: OTPConfirm,
            });
            if (resData.status === 200) {
                toastSuccess('Xoá dữ liệu thành công!')
                setModalDelete({
                    data: null,
                    isOpen: false,
                });
                setOTPConfirm('');
                await handleSearch();
            }
        } catch (error) {
            toastError('Vui lòng kiểm tra lại thông tin!')
        } finally {
            setIsLoading(false);
        }
    }

    const fetchData = useCallback(async (cpCode: string, isClear = false) => {
        try {
            setIsLoading(true)
            const currentCpCode = PrevCpCode && !isClear ? PrevCpCode : cpCode;
            const resSponsors = await apiGetListStatistical({
                CpCode: currentCpCode,
                Year: months ? months.year() : dayjs().year(),
                Month: months ? months.month() + 1 : dayjs().month() + 1,
                Week: null,
                Day: null,
                PageSize: pageSize,
                PageNumber: currentPage,
                SortBy: '',
                SortDesc: true,
            })
            if (resSponsors.data.partnerStatisticals !== null) {
                setStatistical(resSponsors.data.partnerStatisticals)
            } else {
                setStatistical({})
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize])

    const fetchApiGetListPartner = useCallback(async () => {
        try {
            setIsLoading(true)
            const resCustomers = await apiGetListPartner()
            if (resCustomers.data.partners !== null) {
                resCustomers.data.partners = resCustomers.data.partners.filter((item: any) => !mapCompanyAccounts[item.cpCode])
                if (customers.partners.length === 0) {
                    await fetchData(resCustomers.data.partners[0]?.cpCode)
                    if (PrevCpCode) {
                        setSelectedCustomerCode(resCustomers.data.partners.findIndex((item: any) => item.cpCode === PrevCpCode))
                    }
                }
                setCustomers(resCustomers.data)
            } else {
                setCustomers(initCustomer)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        if (customers.partners[0]?.cpCode) {
            fetchData(customers.partners[0].cpCode)
        } else {
            fetchApiGetListPartner()
        }
    }, [fetchData])

    const clearEventCode = async () => {
        setSelectedCustomerCode(0)
        setSelectedTypeReport("typeMonth")
        setMonths(dayjs())
        try {
            setIsLoading(true)
            await fetchData(customers.partners[0].cpCode ?? '', true);
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSearch = async () => {
        try {
            setIsLoading(true)
            let body = {
                CpCode: customers.partners[selectedCustomerDataCode]?.cpCode,
                Year: months ? months.year() : dayjs().year(),
                Month: months ? months.month() + 1 : dayjs().month() + 1,
                Week: null,
                Day: null,
                PageSize: pageSize,
                PageNumber: currentPage,
                SortBy: '',
                SortDesc: true,
            }
            if (selectedTypeReport === "typeDay") {
                body.Year = dates ? dates.year() : dayjs().year();
                body.Month = dates ? dates.month() + 1 : dayjs().month() + 1;
                body.Day = dates ? dates.date() : dayjs().date();
            }
            const resSponsors = await apiGetListStatistical(body)
            if (resSponsors.data.partnerStatisticals != null) {
                setStatistical(resSponsors.data.partnerStatisticals)
            } else {
                setStatistical({})
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const onChangeTypeReport = (value: string) => {
        setSelectedTypeReport(value);
        value === "typeMonth" ? setMonths(dayjs()) : setDates(dayjs().subtract(1, 'day'));
    }

    const onChangeMonth: DatePickerProps['onChange'] = (date, dateStrings) => {
        setMonths(date);
    };

    const onChangeDate: DatePickerProps['onChange'] = (date, dateStrings) => {
        setDates(date);
    };

    return (
        <div style={{ overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <div style={{ display: 'flex', marginBottom: 10, marginRight: 30, alignItems: 'baseline' }}>
                    <span style={{ width: 80 }}>Chọn đối tác</span>
                    <Select
                        showSearch
                        style={{ flex: 1, marginBottom: 16, minWidth: '150px' }}
                        placeholder="Chọn khách hàng"
                        optionFilterProp="name"
                        onChange={(value) => setSelectedCustomerCode(value)}
                        filterOption={(input: any, option: any) =>
                            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={selectedCustomerDataCode}
                    >
                        {customers?.partners?.map((customer: any, idx: React.Key) => (
                            <Select.Option key={idx} value={idx}>
                                {customer.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ display: 'flex', marginBottom: 10, marginRight: 30, alignItems: 'baseline' }}>
                    <span style={{ width: 110 }}>Chọn loại báo cáo</span>
                    <Select
                        showSearch
                        style={{ flex: 1, marginBottom: 16, minWidth: '150px' }}
                        placeholder="Chọn loại báo cáo"
                        optionFilterProp="name"
                        onChange={onChangeTypeReport}
                        filterOption={(input: any, option: any) =>
                            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={selectedTypeReport}
                    >
                        {typeReport?.map((data: any, idx: React.Key) => (
                            <Select.Option key={idx} value={data.value}>
                                {data.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ display: 'flex', marginBottom: 10, marginRight: 30, alignItems: 'baseline' }}>
                    <span style={{ width: 100 }}>Chọn thời gian</span>
                    <Space direction="vertical">
                        {selectedTypeReport === "typeMonth" &&
                            <DatePicker
                                value={months}
                                onChange={onChangeMonth}
                                format="YYYY-MM"
                                picker="month"
                            />}
                        {selectedTypeReport === "typeDay" &&
                            <DatePicker
                                value={dates}
                                onChange={onChangeDate}
                                format={dateFormat}
                                picker="date"
                            />}
                    </Space>
                    <Space direction="vertical">
                    </Space>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <Button type="primary" block style={{ width: 100, marginRight: 10 }} onClick={handleSearch}>Thống kê</Button>
                    <Button danger block style={{ width: 100, marginRight: 10 }} onClick={clearEventCode}>Clear</Button>
                    <Link to={REVENUES + STATISTICAL + CREATE_STATISTICAL + `?cpCode=${customers.partners[selectedCustomerDataCode]?.cpCode}`}>
                        <Button type="primary" block style={{width: 100}} >Thêm mới</Button>
                    </Link>
                </div>
            </div>
            <CustomTable
                namePage='data'
                columns={columns}
                dataSource={statistical}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={statistical.length}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />

            <ViewStatistical
                isOpen={isModalViewOpen}
                id={currentStatistical}
                setOpenModal={setIsModalViewOpen}
            />

            <Modal title='Delete Statistical' open={modalDelete.isOpen}
                   onCancel={() => {
                       setModalDelete({
                            data: null,
                            isOpen: false,
                       })
                       setOTPConfirm('')
                   }}
                   footer={[
                       <Button key={modalDelete.data?.id} onClick={async () => handleDelete(modalDelete.data?.id)} type='primary'>
                           Xoá
                       </Button>
                   ]}>
                <div style={{ marginBottom: 20 }}>
                    Bạn có chắc muốn xoá dữ liệu ngày <b>{getDate(modalDelete.data)}</b> cho đối tác <b>{modalDelete.data?.cpCode}</b>?
                </div>
                <Input
                    placeholder="Mã OTP xác nhận"
                    value={OTPConfirm}
                    onChange={e => setOTPConfirm(e.target.value)}
                />
            </Modal>

        </div>
    )
}
